import { useEffect } from "react";
import { initPropertyPageTracking, trackValuationRequest, trackCTAClick } from "@/lib/analytics";
import ReviewsSection from "../home/ReviewsSection";
import LocationsCarousel from "@/components/LocationsCarousel";
import GuaranteedRentHero from "./GuaranteedRentHero";
import GuaranteedRentComparison from "./GuaranteedRentComparison";
import GuaranteedRentHowItWorks from "./GuaranteedRentHowItWorks";
import GuaranteedRentBenefits from "./GuaranteedRentBenefits";
import GuaranteedRentFAQ from "./GuaranteedRentFAQ";

export default function GuaranteedRentPage({ city }) {
  const { name, slug } = city;
  const path = `/guaranteed-rent/${slug}/`;
  const lowerName = name.toLowerCase();

  useEffect(() => {
    const cleanup = initPropertyPageTracking(path);

    window.trackValuationRequest = () => trackValuationRequest(`${name} Guaranteed Rent`);
    window.trackCTAClick = (type) => trackCTAClick(type, `${name} Guaranteed Rent`);

    document.title = `Guaranteed Rent ${name} | Fixed Monthly Income | Truestays`;

    const setMeta = (nameAttr, content) => {
      let el = document.querySelector(`meta[name="${nameAttr}"]`);
      if (!el) { el = document.createElement("meta"); el.name = nameAttr; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setOgMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    setMeta("description", `${name} guaranteed rent from £1,100–£5,500/month. Fixed income, zero hassle, no voids. Professional property management included. Free valuation.`);
    setMeta("robots", "index, follow");
    setMeta("keywords", `guaranteed rent ${lowerName}, fixed rent ${lowerName}, property management ${lowerName}, rental income ${lowerName}, landlord services ${lowerName}, rent guarantee ${lowerName}`);
    setOgMeta("og:type", "website");
    setOgMeta("og:title", `Guaranteed Rent ${name} | Fixed Monthly Income | Truestays`);
    setOgMeta("og:description", `Earn £1,100–£5,500/month guaranteed in ${name}. Fixed income, zero voids, full property management included. Get your free valuation.`);
    setOgMeta("og:url", window.location.origin + path);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = `https://truestays.co.uk${path}`;

    const schemaId = `ts-schema-gr-${slug}`;
    let existingScript = document.getElementById(schemaId);
    if (existingScript) existingScript.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "LocalBusiness", "@id": `https://truestays.co.uk${path}`, "name": `Truestays — Guaranteed Rent ${name}`, "description": `Fixed monthly rental income in ${name} with full property management included. No voids, no hassle.`, "url": `https://truestays.co.uk${path}`, "priceRange": "From £1,100–£5,500/month", "areaServed": { "@type": "City", "name": name }, "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "96", "bestRating": "5" } },
        { "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://truestays.co.uk/" }, { "@type": "ListItem", "position": 2, "name": "Guaranteed Rent", "item": "https://truestays.co.uk/guaranteed-rent/" }, { "@type": "ListItem", "position": 3, "name": name, "item": `https://truestays.co.uk${path}` }] }
      ]
    });
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
      cleanup();
    };
  }, [path, name, lowerName, slug]);

  return (
    <>
      <GuaranteedRentHero city={city} />
      <GuaranteedRentComparison city={city} />
      <GuaranteedRentBenefits />
      <GuaranteedRentHowItWorks />
      <ReviewsSection />
      <GuaranteedRentFAQ city={city} />
      <LocationsCarousel currentLocation={name} />
    </>
  );
}